/* eslint-disable @typescript-eslint/no-explicit-any */
import { Grid } from '@mui/material';
import React, { Fragment, useEffect, useRef, useState } from 'react';
import styles from './RowSection.module.css';
import {
  RowSection as RowSectionType,
  addRowContentBlock,
  clearSelectedItem,
  deleteRowById,
  selectPageStyles,
} from 'features/document/documentSlice';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import ColumnSection from '../ColumnSection/ColumnSection';
import { useDrag, useDrop, DragPreviewImage } from 'react-dnd';
import { XYCoord } from 'dnd-core';
import SelectedItem from 'features/document/components/SelectedItem/SelectedItem';
import { boxImage } from './BoxImage';
import { ElementSide, PagePartType } from 'features/document/constants';
import { useDropUpdateContext } from 'pages/DocumentBuilder/components/DropContext';
interface Props {
  pageId: string;
  index: number;
  row: RowSectionType;
  moveSection: (pageId: string, index: number, isTop: boolean) => void;
}

interface DragItem {
  row: RowSectionType;
  pageId: string;
  index: number;
}

type DraggableTypes = { type: PagePartType.Block; elementType: string } | ({ type: PagePartType.Row } & DragItem);

const RowSection: React.FC<Props> = ({ pageId, row, index, moveSection }) => {
  const dispatch = useAppDispatch();
  const updateStatus = useDropUpdateContext();
  const pageStyles = useAppSelector(state => selectPageStyles(state, pageId));
  useEffect(() => {
    if (row.columns.length === 0) dispatch(deleteRowById({ rowId: row.id, pageId }));
  }, [row, pageId, dispatch]);

  const ref = useRef<any>(null);

  const [borderHighlight, setBorderHighlight] = useState({
    top: false,
    bottom: false,
  });

  const [{ isOver, isOverCurrent }, drop] = useDrop<DraggableTypes, void, { isOver: boolean; isOverCurrent: boolean }>(
    () => ({
      accept: [PagePartType.Row, PagePartType.Block],
      collect: monitor => ({
        isOver: monitor.isOver(),
        isOverCurrent: monitor.isOver({ shallow: true }),
      }),
      hover: (item, monitor) => {
        if (!ref.current) {
          return;
        }
        updateStatus({ page: false, row: true, element: false });

        const isRow = item.type === PagePartType.Row;
        if (isRow && row.id === item.row.id) {
          return;
        }
        const clientOffset = monitor.getClientOffset();
        const hoverBoundingRect = ref.current?.getBoundingClientRect();
        const middleRect = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
        const hoverClientY = (clientOffset as XYCoord).y - hoverBoundingRect.top;
        setBorderHighlight({
          top: isOver && isOverCurrent && !isDragging && hoverClientY < middleRect,
          bottom: isOver && isOverCurrent && !isDragging && hoverClientY > middleRect,
        });
      },
      drop: (item, _monitor) => {
        const isRow = item.type === PagePartType.Row;
        const isBlock = item.type === PagePartType.Block;

        if (borderHighlight.top) {
          if (isRow) moveSection(pageId, index, true);
          else if (isBlock) {
            dispatch(
              addRowContentBlock({
                pageId,
                rowIndex: index,
                side: ElementSide.Top,
                type: item.elementType,
              })
            );
          }
        } else if (borderHighlight.bottom) {
          if (isRow) moveSection(pageId, index, false);
          else if (isBlock) {
            dispatch(
              addRowContentBlock({
                pageId,
                rowIndex: index,
                side: ElementSide.Bottom,
                type: item.elementType,
              })
            );
          }
        }

        dispatch(clearSelectedItem({}));
      },
    }),
    [borderHighlight]
  );

  const [{ isDragging }, drag, preview] = useDrag(() => ({
    type: PagePartType.Row,
    item: { pageId, row, index, type: PagePartType.Row },
    collect: monitor => ({
      isDragging: !!monitor.isDragging(),
    }),
  }));

  drop(ref);

  //TODO: CHANGE this preview for the correct ones according section type
  return (
    <Fragment>
      <DragPreviewImage connect={preview} src={boxImage} />
      <div
        style={{
          opacity: isDragging ? 0.5 : 1,
        }}
        ref={ref}
        className={styles.mainContainer}
      >
        {isOver && isOverCurrent && !isDragging && (
          <>
            <div className={styles.containerLineBot}>
              <div className={styles.line} style={{ opacity: borderHighlight.bottom ? 1 : 0.5 }} />
            </div>
            <div className={styles.containerLineTop}>
              <div className={styles.line} style={{ opacity: borderHighlight.top ? 1 : 0.5 }} />
            </div>
          </>
        )}
        <SelectedItem data={{ data: row, pageId, index, type: PagePartType.Row }} ref={drag} isDragging={isDragging}>
          <Grid flexDirection='column' sx={{ paddingX: pageStyles?.addSheetPadding ? '50px' : '0px' }}>
            <Grid item>
              <Grid container justifyContent='space-around' flexWrap='nowrap'>
                {row.columns.map((column, index) => {
                  return (
                    //TODO: add component to resize columns
                    <Grid key={column.id} item xs>
                      <ColumnSection rowId={row.id} pageId={pageId} index={index} column={column} />
                    </Grid>
                  );
                })}
              </Grid>
            </Grid>
          </Grid>
        </SelectedItem>
      </div>
    </Fragment>
  );
};

export default RowSection;
